import Highcharts, { type PointOptionsObject } from "highcharts";
import React from "react";
import type { ActiveAnalysis } from "../../../../../../api/useActiveAnalysesStream";
import type { Setup } from "../../../../../../api/useSetups";
import { getFailureSerie } from "../../../../Chart/series/getFailureSeries";
import type { IndexedFailureValues } from "../../../../../../api/useIndexedFailureValues";
import type { IndexedFailureMetadata } from "../../../../../../api/useFailureMetadata";
import type { FailureValueStatus } from "../../../../Chart/PointMetadata";
import type { Quantity } from "../../../../../../api/useQuantities";
import { indexBy } from "diagnostics-lib/iterable";
import { getMostRecentDisplayNames } from "../../../../../../domain/failures";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import seriesColors from "../../../../Chart/series/seriesColors";
import type { ManualFailureValue } from "../../../../../../api/useManualFailureValues";
import { useSettings } from "../../../../../../api/useSettings";
import { getManualFailureSerie } from "../../../../Chart/series/getManualFailuresSeries";

interface BaseSparklineEvent {
  id: string;
  startsAt: number;
  endsAt: number;
}

export type SparklineEvent = BaseSparklineEvent &
  (
    | {
        type: "DIAGNOSED";
        failures: {
          name: string;
          occurrencePeriods: { startsAt: number; endsAt: number }[];
        }[];
      }
    | {
        type: "MANUAL";
        failureId: string;
        failureName: string;
      }
  );

const getFailureSeries = ({
  yAxisId,
  setups,
  analyses,
  qoi,
  indexedFailureValues,
  indexedFailureMetadata,
  failureName,
}: {
  yAxisId: string;
  setups: Setup[];
  analyses: ActiveAnalysis[];
  qoi: Quantity | null;
  indexedFailureValues: IndexedFailureValues;
  indexedFailureMetadata: IndexedFailureMetadata;
  failureName: string;
}): Highcharts.SeriesAreaOptions => {
  const failureByNameBySetupId = indexBy(
    setups.map(setup => ({
      setupId: setup.id,
      failureByName: indexBy(setup.failures, "name"),
    })),
    "setupId",
  );

  const observableByQuantityIdBySetupId = indexBy(
    setups.map(setup => ({
      setupId: setup.id,
      observableByQuantityId: indexBy(setup.diagnosisInputs, "quantityId"),
    })),
    "setupId",
  );

  const impactedObsCounts = analyses.reduce<{ analysisId: string; count: number }[]>(
    (acc, analysis) => {
      const setup = getAnalysisSetup(setups, analysis);

      if (setup !== null) {
        const impactedObs = setup.diagnosisInputs.filter(input => {
          const quantityValue = analysis.quantityValues[input.quantityId];
          return input.enabled && quantityValue !== undefined && quantityValue.simulated !== null;
        });

        acc.push({
          analysisId: analysis.id,
          count: impactedObs.length,
        });
      }

      return acc;
    },
    [],
  );

  const displayNameByFailureName = getMostRecentDisplayNames(setups);

  const impactedObsCountByAnalysisId = indexBy(impactedObsCounts, "analysisId");
  const faultSeries = getFailureSerie({
    type: "area",
    failureSerieType: "OBSERVABLE",
    indexedFailureValues,
    indexedFailureMetadata,
    yAxisId,
    failureName,
    color: seriesColors.sparklineArea,
    analyses,
    setups,
    displayNameByFailureName,
    qoi,
    failureByNameBySetupId,
    observableByQuantityIdBySetupId,
    legendIndex: 0,
    impactedObsCountByAnalysisId,
    actionAllowed: false,
    failureValueStatusFilter: emptySet,
  }) as Highcharts.SeriesAreaOptions;

  const data = (faultSeries.data as PointOptionsObject[]).map(point => {
    if (point.y === null || point.marker?.symbol === "empty") return point;
    return {
      ...point,
      marker: {
        enabled: true,
        symbol: "circle",
        lineWidth: 0,
        fillColor: seriesColors.sparklineMarker,
        lineColor: seriesColors.sparklineMarker,
        radius: 2,
      },
    };
  });

  return {
    ...faultSeries,
    data,
    replicateInNavigator: false,
    zones: [],
    lineColor: seriesColors.sparklineArea,
    fillColor: seriesColors.fadeSparklineArea,
  } as Highcharts.SeriesAreaOptions;
};

const getManualFailureSeries = ({
  yAxisId,
  manualFailureName,
  manualFailureId,
  analyses,
  qoi,
  indexedManualFailureValues,
}: {
  yAxisId: string;
  manualFailureName: string;
  manualFailureId: string;
  analyses: ActiveAnalysis[];
  qoi: Quantity | null;
  indexedManualFailureValues: {
    [analysisId: string]: { [manualFailureId: string]: ManualFailureValue };
  };
}): Highcharts.SeriesAreaOptions => {
  const serie = getManualFailureSerie({
    indexedManualFailureValues,
    yAxisId,
    manualFailureName,
    manualFailureId,
    color: seriesColors.sparklineMarker,
    analyses,
    qoi,
    legendIndex: 0,
    actionAllowed: false,
    type: "area",
    replicateInNavigator: false,
  });

  const data = (serie.data as PointOptionsObject[]).map(point => {
    if (point.y === null || point.marker?.symbol === "empty") return point;
    return { ...point, marker: { enabled: true, symbol: "circle", lineWidth: 0, radius: 2 } };
  });

  return {
    ...serie,
    data,
    lineColor: seriesColors.sparklineArea,
    fillColor: seriesColors.fadeSparklineArea,
  } as Highcharts.SeriesAreaOptions;
};

export const applyOccurrencePeriodsToSeries = (
  serie: Highcharts.SeriesAreaOptions,
  occurrencePeriods: { startsAt: number; endsAt: number }[],
): Highcharts.SeriesAreaOptions => ({
  ...serie,
  data: ((serie.data ?? []) as PointOptionsObject[]).map(point => {
    const date = point.x;
    const isInOccurrencePeriod =
      date !== undefined &&
      occurrencePeriods.some(period => date >= period.startsAt && date <= period.endsAt);

    return {
      ...point,
      y: isInOccurrencePeriod ? point.y : null,
    };
  }),
});

export const aggregateFailureSeries = (
  series: Highcharts.SeriesAreaOptions[],
): Highcharts.SeriesAreaOptions | null => {
  const firstSeries = series[0];
  if (firstSeries === undefined) return null;

  const pointsByDate = new Map<
    number,
    { point: PointOptionsObject; total: number; hasValue: boolean }
  >();

  for (const serie of series) {
    for (const point of (serie.data ?? []) as PointOptionsObject[]) {
      if (point.x === undefined) continue;

      const current = pointsByDate.get(point.x) ?? {
        point,
        total: 0,
        hasValue: false,
      };

      if (typeof point.y === "number") {
        current.point = point;
        current.total += point.y;
        current.hasValue = true;
      }

      pointsByDate.set(point.x, current);
    }
  }

  const data = Array.from(pointsByDate.entries())
    .sort(([dateA], [dateB]) => dateA - dateB)
    .map(([, { point, total, hasValue }]) => ({
      ...point,
      y: hasValue ? total : null,
      ...(hasValue
        ? {
            marker: {
              enabled: true,
              symbol: "circle",
              lineWidth: 0,
              fillColor: seriesColors.sparklineMarker,
              lineColor: seriesColors.sparklineMarker,
              radius: 2,
            },
          }
        : {}),
    }));

  return {
    ...firstSeries,
    data,
    lineColor: seriesColors.sparklineArea,
    fillColor: seriesColors.fadeSparklineArea,
  };
};

const emptySet = new Set<FailureValueStatus>();
const useSeries = ({
  yAxisId,
  setups,
  analyses,
  indexedFailureValues,
  indexedManualFailureValues,
  indexedFailureMetadata,
  qoi,
  events,
}: {
  yAxisId: string;
  setups: Setup[];
  analyses: ActiveAnalysis[];
  indexedFailureValues: IndexedFailureValues;
  indexedManualFailureValues: {
    [analysisId: string]: { [manualFailureId: string]: ManualFailureValue };
  };
  indexedFailureMetadata: IndexedFailureMetadata;
  qoi: Quantity | null;
  events: SparklineEvent[];
}): {
  id: string;
  axisId: string;
  startsAt: number;
  endsAt: number;
  series: (Highcharts.SeriesLineOptions | Highcharts.SeriesAreaOptions)[];
}[] => {
  const settings = useSettings();
  const manualEventEnabled = React.useMemo(
    () => settings.MANUAL_EVENTS_DEV_MODE_ENABLED,
    [settings],
  );

  const series = React.useMemo(() => {
    if (analyses.length === 0) return [];

    const result: {
      id: string;
      axisId: string;
      startsAt: number;
      endsAt: number;
      series: (Highcharts.SeriesLineOptions | Highcharts.SeriesAreaOptions)[];
    }[] = [];
    for (const event of events) {
      const { id, startsAt, endsAt } = event;
      const axisId = yAxisId + id + startsAt;

      if (event.type === "MANUAL") {
        if (!manualEventEnabled) continue;

        const serie = getManualFailureSeries({
          yAxisId: axisId,
          manualFailureName: event.failureName,
          manualFailureId: event.failureId,
          analyses,
          qoi,
          indexedManualFailureValues,
        });

        if (!serie.data || serie.data.length === 0) continue;
        if ((serie.data as PointOptionsObject[]).every(p => p.y === null || p.y === undefined))
          continue;

        result.push({ id, axisId, startsAt, endsAt, series: [serie] });
      } else {
        const failureSeries = event.failures.map(failure =>
          applyOccurrencePeriodsToSeries(
            getFailureSeries({
              yAxisId: axisId,
              setups,
              analyses,
              indexedFailureValues,
              indexedFailureMetadata,
              qoi,
              failureName: failure.name,
            }),
            failure.occurrencePeriods,
          ),
        );
        const serie = aggregateFailureSeries(failureSeries);

        if (serie === null || !serie.data || serie.data.length === 0) continue;
        if ((serie.data as PointOptionsObject[]).every(p => p.y === null || p.y === undefined))
          continue;

        result.push({ id, axisId, startsAt, endsAt, series: [serie] });
      }
    }

    return result;
  }, [
    analyses,
    events,
    manualEventEnabled,
    yAxisId,
    indexedManualFailureValues,
    qoi,
    setups,
    indexedFailureValues,
    indexedFailureMetadata,
  ]);

  return series;
};

export default useSeries;
